/**
 * Checklist of static tags rendered as their colored badges (same badge
 * style as the Static tab), with a search box once the list grows.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useMemo, useState } from 'react';

import { Checkbox, Input } from '@/components/ui';
import type { StaticTag } from '@/hooks/smswiser/static-tags/useStaticTags';

const SEARCH_THRESHOLD = 8;

export interface TagPickListProps {
  tags: StaticTag[];
  selectedIds: string[];
  onToggle: (tagId: string) => void;
}

export const TagPickList: FC<TagPickListProps> = ({ tags, selectedIds, onToggle }) => {
  const [search, setSearch] = useState('');
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return tags;
    return tags.filter((t) => t.name.toLowerCase().includes(needle));
  }, [tags, search]);

  if (tags.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No static tags yet — create them under Admin → Tag Rules → Static.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {tags.length > SEARCH_THRESHOLD && (
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tags…"
          className="h-8"
        />
      )}
      <div className="max-h-56 space-y-1 overflow-y-auto pr-1">
        {visible.map((tag) => (
          <label
            key={tag.id}
            className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted/40"
          >
            <Checkbox
              checked={selectedIds.includes(tag.id)}
              onCheckedChange={() => onToggle(tag.id)}
            />
            <span
              className="inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold"
              style={{
                backgroundColor: `${tag.color}15`,
                color: tag.color,
                borderColor: `${tag.color}40`,
              }}
            >
              {tag.name}
            </span>
          </label>
        ))}
        {visible.length === 0 && (
          <p className="px-2 py-1 text-xs text-muted-foreground">No tags match “{search.trim()}”.</p>
        )}
      </div>
    </div>
  );
};
